import type {
  ExperienceCard,
  LinkCreate,
  LinkEntryProblem,
  LinkExperienceLookup,
  LinkLifetime,
  LinkSummary,
  SessionSide,
  VersionPin,
} from '@matter_extractor/shared';
import { LINK_DEFAULT_LIFETIME, linkEntryProblems } from '@matter_extractor/shared';
import { newLocalId } from './protocol-ids';

/**
 * The draft of the « Créer un lien » form, and what greys its button (§11.4).
 *
 * PURE, and outside `src/routes/**` for the reason stated at the head of
 * `persona-settings.ts`: that tree is left out of the coverage report, and the
 * rule that decides whether a link can be created is exactly the kind of rule
 * that must be measured.
 *
 * The refusal is computed HERE, from the experience cards the page already
 * read, and not asked of the server: `linkEntryProblems` is in `shared`
 * precisely so the form greys its button on the very rule the route refuses
 * with. The server still judges - the cards can be stale by a colleague's
 * gesture - and the form shows whichever of the two is in hand.
 */

/**
 * One row of the entries block: a select, a pin, a side, and a LOCAL key.
 *
 * The key never leaves the browser - `linkPayloadOf` drops it - and it exists
 * because a row has no identity of its own otherwise: two rows may legitimately
 * name the same experience at the same pin, and React keyed on the index
 * would remount the row under the operator's cursor the moment one is removed
 * above it. Struck by `newLocalId`, the way a protocol's steps are.
 */
export interface LinkFormRow {
  readonly key: string;
  /** Empty while nothing is chosen - a row exists before it names anything. */
  readonly experienceId: string;
  /** `latest` unless the operator pins a version (vitrine.md §3.4, V16). */
  readonly pin: VersionPin;
  /** The side the session plays on, `dev` unless the operator picks `prod` (V18). */
  readonly side: SessionSide;
}

/** A fresh row, at `latest` on the dev side - the default of every reference (V16). */
function newRow(experienceId = ''): LinkFormRow {
  return { key: newLocalId(), experienceId, pin: 'latest', side: 'dev' };
}

export interface LinkFormDraft {
  readonly name: string;
  readonly lifetime: LinkLifetime;
  readonly entries: readonly LinkFormRow[];
}

/**
 * A fresh form: one empty row, the shortest lifetime, `latest` on the dev side
 * on the row.
 *
 * `latest` by default because it is the default of every reference (vitrine.md
 * V16), on the dev side because the atelier is where a link is made for a test.
 * The pre-fill of §9.1 (« Créer un lien » from an experience's editor) names the
 * experience and leaves the rest alone.
 *
 * A FUNCTION and not a constant: the draft is handed to `useState` and then
 * edited in place by the page, and a shared constant would be one object every
 * fresh form started from.
 */
export function emptyLinkForm(experienceId = ''): LinkFormDraft {
  return { name: '', lifetime: LINK_DEFAULT_LIFETIME, entries: [newRow(experienceId)] };
}

/** The form of an EXISTING link, for « Modifier » - the three editable things (§5.1). */
export function linkFormOf(link: LinkSummary): LinkFormDraft {
  return {
    name: link.name,
    lifetime: link.lifetime,
    entries: link.entries.map((entry) => ({
      key: newLocalId(),
      experienceId: entry.experienceId,
      pin: entry.pin,
      side: entry.side,
    })),
  };
}

/**
 * The form of a link with ONE entry's pin moved - « Mettre à jour » on a pinned
 * entry that is behind (vitrine.md §3.3): the rest of the link is saved as it is.
 */
export function linkFormWithPin(
  draft: LinkFormDraft,
  index: number,
  pin: VersionPin,
): LinkFormDraft {
  return {
    ...draft,
    entries: draft.entries.map((row, at) => (at === index ? { ...row, pin } : row)),
  };
}

/** The body the API takes, once the form is known to be complete. */
export function linkPayloadOf(draft: LinkFormDraft): LinkCreate {
  return {
    name: draft.name.trim(),
    lifetime: draft.lifetime,
    entries: draft.entries.map((row) => ({
      experienceId: row.experienceId,
      pin: row.pin,
      side: row.side,
    })),
  };
}

/**
 * What the base knows about each experience, read off the CARDS the page has.
 *
 * A card carries the three facts the rule needs - the name, whether a
 * production pointer is posed, whether it is retired - so no second read is
 * needed and no endpoint of its own.
 */
export function linkExperienceLookupOf(
  experiences: readonly ExperienceCard[],
): LinkExperienceLookup {
  const byId = new Map(
    experiences.map((card) => [
      card.id,
      { name: card.name, hasProduction: card.production !== null, retiredAt: card.retiredAt },
    ]),
  );
  return (experienceId) => byId.get(experienceId);
}

/**
 * The refused rows of a draft, with the index of the row IN THE FORM.
 *
 * Rows that name nothing yet are left out of the walk and put back out of the
 * indexing: an unchosen select is an incomplete form, not an unknown
 * experience, and « cette expérience n'existe plus » under a row nobody has
 * filled would accuse the operator of something they have not done. It still
 * greys the button - see `canCreateLink`.
 */
export function linkFormProblems(
  draft: LinkFormDraft,
  lookup: LinkExperienceLookup,
): LinkEntryProblem[] {
  const filled = draft.entries
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.experienceId !== '');
  return linkEntryProblems(
    filled.map(({ row }) => row),
    lookup,
  ).map((problem) => ({ ...problem, index: filled[problem.index]?.index ?? problem.index }));
}

/**
 * What arms « Créer le lien » (§11.4): a name, at least one row, every row
 * filled, and no refusal.
 *
 * The refusals are passed IN rather than recomputed, because the page shows the
 * server's list once it has one - « le bouton reste inactif tant que le refus
 * est là » has to hold for the refusal the operator can actually see, whichever
 * of the two it is.
 */
export function canSubmitLink(
  draft: LinkFormDraft,
  problems: readonly LinkEntryProblem[],
): boolean {
  if (draft.name.trim() === '') return false;
  if (draft.entries.length === 0) return false;
  if (draft.entries.some((row) => row.experienceId === '')) return false;
  return problems.length === 0;
}

/* ------------------------------------------------------------- the editions */

/** Adds a row at the end - « Ajouter une entrée » (§11.4). */
export function addLinkRow(draft: LinkFormDraft): LinkFormDraft {
  return { ...draft, entries: [...draft.entries, newRow()] };
}

/**
 * Removes one row. The LAST one is never removed: §5.1 makes an entry-less link
 * impossible, so a form with no row would be a form whose only button is dead -
 * the argument `removeStep` makes about a protocol's last step.
 */
export function removeLinkRow(draft: LinkFormDraft, index: number): LinkFormDraft {
  if (draft.entries.length <= 1) return draft;
  return { ...draft, entries: draft.entries.filter((_row, at) => at !== index) };
}

export function editLinkRow(
  draft: LinkFormDraft,
  index: number,
  patch: Partial<LinkFormRow>,
): LinkFormDraft {
  return {
    ...draft,
    entries: draft.entries.map((row, at) => (at === index ? { ...row, ...patch } : row)),
  };
}

/**
 * A stable key per entry of a SERVED link, for the pastilles of a card.
 *
 * A served entry has no id of its own - the base stores the triple and nothing
 * more - and a link may legitimately carry the same triple twice, so neither
 * the triple nor the position is an identity on its own. The nth OCCURRENCE of a
 * triple is: it survives a re-read, it does not move when a neighbouring entry is
 * edited, and it is not the index wearing a different name.
 */
export function linkEntryKeys(
  entries: readonly { experienceId: string; pin: VersionPin; side: SessionSide }[],
): string[] {
  const seen = new Map<string, number>();
  return entries.map((entry) => {
    const pair = `${entry.experienceId}:${entry.pin}:${entry.side}`;
    const seenBefore = seen.get(pair) ?? 0;
    seen.set(pair, seenBefore + 1);
    return seenBefore === 0 ? pair : `${pair}#${seenBefore}`;
  });
}
